import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  MessagesSquare, Brain, GraduationCap, BarChart3, ShieldCheck,
  FileClock, Download, TriangleAlert, EyeOff,
} from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { data as fData, moeda, plural } from "../lib/formato.js";

const competenciaDeHoje = () => new Date().toISOString().slice(0, 7);

/* ------------------------------------------------------------------ clima */

export function Clima() {
  const pesquisas = useQuery({
    queryKey: ["gestao", "clima", "pesquisas"],
    queryFn: () => trpc.operacao.pesquisasClima.query(),
  });
  const publicacoes = useQuery({
    queryKey: ["gestao", "clima", "publicacoes"],
    queryFn: () => trpc.operacao.publicacoesClima.query({}),
  });

  return (
    <Pagina
      secao="Desenvolvimento" titulo="Clima e eNPS"
      descricao="A tabela de resposta não tem coluna de respondente. O resultado sai agregado com k-anonimato, e a supressão é em cascata: esconder um grupo pequeno sem esconder o complementar não esconde nada."
    >
      {pesquisas.isPending && <Carregando linhas={4} />}
      {pesquisas.isError && <Falha erro={pesquisas.error} tentarDeNovo={() => void pesquisas.refetch()} />}

      {pesquisas.isSuccess && pesquisas.data.length === 0 && (
        <EmptyState
          titulo="Nenhuma pesquisa"
          descricao="A pesquisa nasce com o k mínimo definido, e o k é o que decide se um recorte pode ou não ser publicado."
        />
      )}

      {pesquisas.isSuccess && pesquisas.data.length > 0 && (
        <Cartao titulo="Pesquisas" subtitulo={plural(pesquisas.data.length, "pesquisa", "pesquisas")}>
          <ul className="eop-op">
            {pesquisas.data.map((p) => {
              const taxa = p.convidados > 0
                ? Math.round((p.respondentes / p.convidados) * 100) : 0;
              return (
                <li key={p.id}>
                  <span aria-hidden className="eop-op-icone"><MessagesSquare size={15} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="eop-op-titulo">{p.nome}</span>
                    <span className="eop-op-sub">
                      {p.tipo} · aberta em {fData(p.abertura)}
                      {p.fechamento ? ` até ${fData(p.fechamento)}` : ""}
                      {" · "}k mínimo {p.kMinimo}
                    </span>
                  </span>
                  <span className="pilula">
                    {p.respondentes} de {p.convidados} · {taxa}%
                  </span>
                </li>
              );
            })}
          </ul>
        </Cartao>
      )}

      {publicacoes.isSuccess && publicacoes.data.length > 0 && (
        <Cartao titulo="Resultados publicados"
                subtitulo="Só recortes que passaram do k mínimo. O que não passou não aparece nem como zero.">
          <ul className="eop-op">
            {publicacoes.data.map((p) => (
              <li key={p.id}>
                <span aria-hidden className="eop-op-icone"><MessagesSquare size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className="eop-op-titulo">{p.recorte}</span>
                  <span className="eop-op-sub">
                    {p.pesquisa} · n = {p.n}
                    {p.media ? ` · média ${p.media}` : ""}
                    {p.enps ? ` · eNPS ${p.enps}` : ""}
                  </span>
                </span>
                <span className="num eop-op-dias">{fData(p.publicadoEm)}</span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}
    </Pagina>
  );
}

/* ------------------------------------------------------------ psicossocial */

export function Psicossocial() {
  const inventarios = useQuery({
    queryKey: ["gestao", "psicossocial"],
    queryFn: () => trpc.operacao.inventariosPsicossociais.query(),
  });
  const programas = useQuery({
    queryKey: ["gestao", "programas"],
    queryFn: () => trpc.operacao.programasSst.query(),
  });

  return (
    <Pagina
      secao="Saúde e segurança" titulo="Riscos psicossociais"
      descricao="Obrigatório desde 2025 pela NR-01. O inventário sai dos dados que a empresa já tem aqui: jornada excessiva vinda do ponto, absenteísmo, rotatividade e clima."
    >
      <Cartao titulo="Como este inventário é montado"
              subtitulo="A contenção é estrutural, não uma configuração">
        <ul className="eop-porque">
          <li><strong>Nenhuma saída é alcançável em nível nominal.</strong> Não existe endpoint que devolva risco psicossocial de uma pessoa, e isso é verificado por teste.</li>
          <li><strong>Perfil comportamental não entra aqui.</strong> Nenhum endpoint de SST alcança o perfil individual.</li>
          <li><strong>Supressão em cascata.</strong> Esconder um grupo pequeno sem esconder o complementar não esconde ninguém, porque o total menos os visíveis entrega o suprimido.</li>
        </ul>
      </Cartao>

      {inventarios.isPending && <Carregando linhas={3} />}
      {inventarios.isError && <Falha erro={inventarios.error} tentarDeNovo={() => void inventarios.refetch()} />}

      {inventarios.isSuccess && inventarios.data.length === 0 && (
        <EmptyState
          titulo="Nenhum inventário gerado"
          descricao="O inventário é gerado por competência e por estabelecimento, e usa o k mínimo para decidir o que pode ser publicado."
        />
      )}

      {inventarios.isSuccess && inventarios.data.length > 0 && (
        <Cartao titulo="Inventários" subtitulo="Agregados, nunca nominais">
          <ul className="eop-op">
            {inventarios.data.map((i) => {
              const suprimidos = Array.isArray(i.suprimidos) ? i.suprimidos.length : 0;
              return (
                <li key={i.id}>
                  <span aria-hidden className="eop-op-icone"><Brain size={15} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="eop-op-titulo">
                      {i.competencia}{i.estabelecimento ? ` · ${i.estabelecimento}` : ""}
                    </span>
                    <span className="eop-op-sub">
                      k mínimo {i.kMinimo} · gerado em {fData(i.geradoEm)}
                    </span>
                  </span>
                  {suprimidos > 0 && (
                    <span className="pilula" title="recortes pequenos demais para publicar">
                      <EyeOff aria-hidden size={11} />
                      {plural(suprimidos, "recorte suprimido", "recortes suprimidos")}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Cartao>
      )}

      {programas.isSuccess && programas.data.length > 0 && (
        <Cartao titulo="PGR e PCMSO" subtitulo="Versionados, assinados e com vigência">
          <ul className="eop-op">
            {programas.data.map((g) => (
              <li key={g.id}
                  data-tom={g.diasAteVencer !== null && g.diasAteVencer < 0 ? "critico"
                    : g.diasAteVencer !== null && g.diasAteVencer <= 60 ? "atencao" : undefined}>
                <span aria-hidden className="eop-op-icone"><ShieldCheck size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className="eop-op-titulo">
                    {g.tipo} v{g.versao}{g.estabelecimento ? ` · ${g.estabelecimento}` : ""}
                  </span>
                  <span className="eop-op-sub">
                    {g.responsavel ?? "sem responsável"}
                    {g.registroProfissional ? ` · ${g.registroProfissional}` : ""}
                    {" · vigente desde "}{fData(g.vigenciaInicio)}
                  </span>
                </span>
                {g.diasAteVencer !== null && (
                  <span className="num eop-op-dias"
                        data-tom={g.diasAteVencer < 0 ? "critico" : g.diasAteVencer <= 60 ? "atencao" : undefined}>
                    {g.diasAteVencer < 0 ? `${Math.abs(g.diasAteVencer)}d vencido` : `${g.diasAteVencer}d`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Cartao>
      )}
    </Pagina>
  );
}

/* ------------------------------------------------------------- treinamento */

export function Treinamento() {
  const requisitos = useQuery({
    queryKey: ["gestao", "requisitos"],
    queryFn: () => trpc.operacao.requisitosTreinamento.query(),
  });
  const realizados = useQuery({
    queryKey: ["gestao", "treinamentos"],
    queryFn: () => trpc.operacao.treinamentos.query(),
  });

  return (
    <Pagina
      secao="Desenvolvimento" titulo="Treinamentos"
      descricao="Aqui mora a conformidade: o que é obrigatório por cargo, quando vence e o pacote de evidência. O conteúdo em si fica no Galaxy, que já é um LMS completo."
    >
      {requisitos.isPending && <Carregando linhas={4} />}
      {requisitos.isError && <Falha erro={requisitos.error} tentarDeNovo={() => void requisitos.refetch()} />}

      {requisitos.isSuccess && requisitos.data.length === 0 && (
        <EmptyState
          titulo="Nenhum requisito cadastrado"
          descricao="O requisito diz quem PRECISA fazer. Sem ele, a lista de quem fez não prova nada: a fiscalização pergunta por quem não fez."
        />
      )}

      {requisitos.isSuccess && requisitos.data.length > 0 && (
        <Cartao titulo="Cobertura por requisito"
                subtitulo="A pergunta da fiscalização é quem NÃO fez, não quem fez">
          <ul className="eop-op">
            {requisitos.data.map((r) => (
              <li key={r.id}
                  data-tom={r.semRegistro > 0 || r.vencidos > 0 ? "critico" : undefined}>
                <span aria-hidden className="eop-op-icone"><GraduationCap size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className="eop-op-titulo">
                    {r.nome}{r.norma ? ` · ${r.norma}` : ""}
                  </span>
                  <span className="eop-op-sub">
                    {r.escopo === "todos" ? "todo o quadro" : `${r.escopo}: ${r.escopoValor}`}
                    {r.cargaHorariaMin ? ` · ${Math.round(r.cargaHorariaMin / 60)}h` : ""}
                    {r.validadeMeses ? ` · vale ${r.validadeMeses} meses` : " · sem validade"}
                  </span>
                </span>
                <span className="eop-cobertura">
                  <span className="pilula" title="em dia">{r.emDia} em dia</span>
                  {r.vencidos > 0 && <span className="pilula">{r.vencidos} vencidos</span>}
                  {r.semRegistro > 0 && <span className="pilula">{r.semRegistro} sem registro</span>}
                </span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {realizados.isSuccess && realizados.data.length > 0 && (
        <Cartao titulo="Realizados" subtitulo="O pacote de evidência que a fiscalização pede">
          <ul className="eop-op">
            {realizados.data.map((t) => (
              <li key={t.id}
                  data-tom={t.diasAteVencer !== null && t.diasAteVencer < 0 ? "critico"
                    : t.diasAteVencer !== null && t.diasAteVencer <= 30 ? "atencao" : undefined}>
                <span aria-hidden className="eop-op-icone"><GraduationCap size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className="eop-op-titulo">{t.nome} · {t.treinamento}</span>
                  <span className="eop-op-sub">
                    {fData(t.realizadoEm)}
                    {t.cargaHorariaMin ? ` · ${Math.round(t.cargaHorariaMin / 60)}h` : ""}
                    {t.instrutor ? ` · ${t.instrutor}` : ""}
                    {/* Certificado sem lista de presença não fecha o pacote em
                        treinamento presencial de NR. */}
                    {!t.temCertificado ? " · SEM certificado" : ""}
                    {!t.temListaPresenca ? " · sem lista de presença" : ""}
                  </span>
                </span>
                {t.diasAteVencer !== null && (
                  <span className="num eop-op-dias"
                        data-tom={t.diasAteVencer < 0 ? "critico" : t.diasAteVencer <= 30 ? "atencao" : undefined}>
                    {t.diasAteVencer < 0 ? `${Math.abs(t.diasAteVencer)}d vencido` : `${t.diasAteVencer}d`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Cartao>
      )}
    </Pagina>
  );
}

/* -------------------------------------------------------------- relatórios */

const MOEDA = new Set(["Folha bruta", "Folha líquida"]);

export function Relatorios() {
  const [competencia, setCompetencia] = useState(competenciaDeHoje());
  const q = useQuery({
    queryKey: ["gestao", "numeros", competencia],
    queryFn: () => trpc.operacao.numerosDeGestao.query({ competencia }),
  });

  return (
    <Pagina
      secao="Gestão" titulo="Relatórios"
      descricao="Todos os números saem da mesma consulta que o painel usa. Duas consultas para a mesma métrica é como o painel, o relatório e o arquivo exportado divergem em um ano."
      acoes={
        <input type="month" className="campo h-8 w-auto text-xs" value={competencia}
               aria-label="Competência"
               onChange={(e) => setCompetencia(e.target.value)} />
      }
    >
      {q.isPending && <Carregando linhas={6} />}
      {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}

      {q.isSuccess && (
        <Cartao titulo={`Números de ${competencia}`}
                subtitulo="Mesma definição no painel, aqui e na exportação">
          <dl className="eop-numeros">
            {q.data.map((n) => (
              <div key={n.rotulo}>
                <dt>{n.rotulo}</dt>
                <dd className="num">
                  {MOEDA.has(n.rotulo) ? moeda(n.valor) : n.valor}
                </dd>
                {n.detalhe && <p className="eop-numeros-nota">{n.detalhe}</p>}
              </div>
            ))}
          </dl>
        </Cartao>
      )}
    </Pagina>
  );
}

/* ----------------------------------------------------------- AFD e AEJ */

export function ArquivosFiscais() {
  return (
    <Pagina
      secao="Obrigações" titulo="AFD e AEJ"
      descricao="O AFD é extraído por estabelecimento e é o que o auditor pede no local. O AEJ é o espelho eletrônico da jornada apurada."
    >
      <Cartao titulo="O que o sistema garante hoje"
              subtitulo="A geração depende de marcação registrada, e a base ainda não tem">
        <ul className="eop-porque">
          <li><strong>NSR sequencial por REP, sem buraco.</strong> A numeração sai de um contador travado no próprio REP, e a unicidade é imposta por uma tabela fora da partição: o mesmo NSR não pode existir em dois meses.</li>
          <li><strong>A marcação nunca é alterada.</strong> `UPDATE` e `DELETE` levantam exceção no banco. Correção é linha nova apontando a original, e as duas ficam no arquivo.</li>
          <li><strong>Layout como dado.</strong> AFD v004 tem 8 tipos posicionais com CRC-16 e SHA-256 encadeado; o AEJ v002 é delimitado por pipe. Os dois são especificação carregada, não código, e o leiaute vive no portal gov.br e muda sem portaria nova.</li>
          <li><strong>Geração em streaming.</strong> Um ano de 500 pessoas é cerca de 1,2 milhão de registros: montar a string em memória é falta de memória garantida.</li>
        </ul>
      </Cartao>

      <EmptyState
        titulo="Nada a exportar ainda"
        descricao="A extração precisa de marcação registrada no período. Assim que houver batida no ponto, o arquivo passa a ser gerado por estabelecimento e por intervalo."
      />

      <Cartao titulo="Por que o gerador não é opcional"
              subtitulo="Vale inclusive no REP-A">
        <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
          Desde a Portaria 1.486/2022, o REP-A também gera AFD. Não existe caminho
          em que o sistema fique sem gerador, e é por isso que a imutabilidade da
          marcação foi decidida antes da primeira batida existir: se a marcação
          aceitasse alteração agora, migrar para REP-P exigiria renumerar o
          histórico inteiro, o que é impossível depois que houver assinatura.
        </p>
      </Cartao>
    </Pagina>
  );
}
